import { useEffect, useRef, useCallback } from 'react';
import useMousePosition from './useMousePosition';
import useIntersectionObserver from './useIntersectionObserver';
import useReducedMotion from './useReducedMotion';

/**
 * Enhanced parallax hook with mouse tracking, performance optimizations, and accessibility
 * @param {object} options - Configuration options
 * @param {number} options.scrollSpeed - Parallax speed multiplier (0.1 = slow, 1.0 = fast)
 * @param {number} options.mouseSpeed - Mouse tracking speed multiplier
 * @param {boolean} options.enableMouse - Whether to enable mouse tracking
 * @param {boolean} options.enableScroll - Whether to enable scroll parallax
 * @param {string} options.axis - Parallax axis ('y', 'x', 'both')
 * @param {number} options.mouseInfluence - How much mouse movement affects the element (0-1)
 * @param {object} options.intersectionOptions - Options for intersection observer
 * @returns {object} - { ref, isVisible, hasIntersected, mousePosition }
 */
const useAdvancedParallax = ({
  scrollSpeed = 0.5,
  mouseSpeed = 0.1,
  enableMouse = true,
  enableScroll = true,
  axis = 'y',
  mouseInfluence = 0.3,
  intersectionOptions = {}
} = {}) => {
  const elementRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastScrollY = useRef(0);
  const lastMouseX = useRef(0);
  const lastMouseY = useRef(0);
  
  // Get mouse position and motion preferences
  const { x: mouseX, y: mouseY, isMouseMoving } = useMousePosition(enableMouse);
  const { isReducedMotion } = useReducedMotion();
  const { isIntersecting, hasIntersected } = useIntersectionObserver(intersectionOptions);

  // Smooth scroll handling with requestAnimationFrame
  const updateTransform = useCallback(() => {
    if (!elementRef.current || isReducedMotion) return;

    const element = elementRef.current;
    let translateX = 0;
    let translateY = 0;
    
    

    // Scroll-based parallax
    if (enableScroll && isIntersecting) {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -scrollSpeed;
      
      if (axis === 'y' || axis === 'both') {
        translateY = rate;
      }
      if (axis === 'x' || axis === 'both') {
        translateX = rate * 0.5; // Horizontal scroll is usually less pronounced
      }
    }

    // Mouse-based parallax
    if (enableMouse && isMouseMoving && hasIntersected) {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      
      // Calculate mouse offset from center
      const mouseOffsetX = (mouseX - centerX) * mouseSpeed * mouseInfluence;
      const mouseOffsetY = (mouseY - centerY) * mouseSpeed * mouseInfluence;
      
      // Apply mouse influence
      if (axis === 'x' || axis === 'both') {
        translateX += mouseOffsetX;
      }
      if (axis === 'y' || axis === 'both') {
        translateY += mouseOffsetY;
      }
    }

    // Apply transforms
    element.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
    
    // Store current values for next frame
    lastScrollY.current = window.pageYOffset;
    lastMouseX.current = mouseX;
    lastMouseY.current = mouseY;
  }, [
    scrollSpeed,
    mouseSpeed,
    enableMouse,
    enableScroll,
    axis,
    mouseInfluence,
    isReducedMotion,
    isIntersecting,
    hasIntersected,
    mouseX,
    mouseY,
    isMouseMoving
  ]);

  // Throttled scroll handler
  const handleScroll = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(updateTransform);
  }, [updateTransform]);

  // Mouse movement handler
  const handleMouseMove = useCallback(() => {
    if (isMouseMoving && hasIntersected) {
      updateTransform();
    }
  }, [isMouseMoving, hasIntersected, updateTransform]);

  useEffect(() => {
    if (isReducedMotion) {
      // Reset transform for reduced motion users
      if (elementRef.current) {
        elementRef.current.style.transform = 'translate3d(0, 0, 0)';
      }
      return;
    }

    // Add event listeners
    if (enableScroll) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    if (enableMouse) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
    }

    // Initial call
    updateTransform();

    return () => {
      // Cleanup
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      if (enableScroll) {
        window.removeEventListener('scroll', handleScroll);
      }
      
      if (enableMouse) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [
    enableScroll,
    enableMouse,
    isReducedMotion,
    handleScroll,
    handleMouseMove,
    updateTransform
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    ref: elementRef,
    isVisible: isIntersecting,
    hasIntersected,
    mousePosition: { x: mouseX, y: mouseY },
    isMouseMoving
  };
};

export default useAdvancedParallax;
